import theme from '../../../theme';

/**
 * Shared visual tokens for the practice map.
 *
 * Everything with a home in the MUI theme is derived from it here rather than
 * re-typed as a hex literal. The map used to carry 37 hardcoded colours, three of
 * which were hand-copied theme values that had already drifted out of anyone's
 * reach — the comments admitted as much ("Matches theme.palette.success.main").
 * Deriving them means the map follows a palette change instead of silently
 * disagreeing with the rest of the app.
 */

/** The one brand blue. Every level used to map to its own entry in a LEVEL_COLOR
 *  lookup, but all three held the identical value — a level-coloured API over a
 *  single-colour design. Green is still reserved for the "done" state. */
export const BRAND = theme.palette.primary.main;
export const DONE = theme.palette.success.main;

/** The road between the two level cards and the A1/B1 switch. A neutral grey, not
 *  BRAND: those legs cross from one card into another, so they read as a different
 *  kind of link than the navy roads running between sections inside a card. */
export const LEVEL_ROAD = '#98A2B3';

/** Pastel blue surface behind an icon that keeps its full-strength colour. */
export const ICON_SURFACE = theme.palette.background.paper;

/**
 * Decorative icons sit in the pastel end of the brand blue rather than the full
 * brand colour: sixteen saturated glyphs in one grid compete with the topic names,
 * which are what you actually read to choose. Two steps up from the ICON_SURFACE
 * tone — that one drops to ~1.35:1 on white and the glyphs disappear.
 */
export const ICON_QUIET = '#8FAFD4';

/**
 * The neutral tile a pastel icon sits on. ICON_SURFACE is itself a pastel blue, so
 * a pastel glyph on it lands near 1.5:1 and effectively disappears — icon tiles use
 * this near-white instead, which is what the sidebar's tiles already do.
 */
export const ICON_TILE = '#F4F6FA';

export const LOCKED_SPOT = '#C3CBD8';
export const LOCKED_BASE = '#F0F3F8';

/**
 * The fill of the "A2 Topic" pill on a topic circle. The mid blue rather than BRAND:
 * it has to be seen at a glance without outweighing the topic name it sits beside.
 */
export const A2_TAG_BLUE = theme.palette.secondary.main;

/**
 * The level badge on a selected material tile ("B1 only").
 *
 * A pastel mint. The badge only ever shows on the *selected* tile, whose fill is
 * brand navy, so the fill has to be light and non-blue — a blue of any tone sinks
 * into the tile, and amber read as a warning. Mint is the page's existing "good
 * news" hue (it is STAMP_PASTEL's neighbour), and its ink clears 7:1 on it.
 */
export const BADGE_PASTEL = '#D8F0E4';
export const BADGE_INK = '#1B6B48';

/** The green partner to ICON_SURFACE, matched for lightness — the completed seal. */
export const STAMP_PASTEL = '#CBEBD6';

/**
 * The green partner to ICON_QUIET — a finished topic's glyph. Matched to it for
 * lightness so a completed bubble reads as the same weight in a different hue
 * rather than as a louder one. Deliberately not STAMP_PASTEL: that tone is a fill
 * behind a mark, and as a glyph on white it drops to ~1.3:1 and disappears.
 */
export const ICON_DONE = '#94D1AD';
export const DONE_TINT = '#EAF6EF';
export const DONE_TINT_SOFT = '#F2FAF5';

/**
 * The grey ramp the map reads text on, darkest first.
 *
 * Deliberately NOT theme.palette.text: the theme's ink is navy (#133E87), which
 * is right for the app's headings but too heavy and too blue for the map's dense
 * body copy and muted labels. This is a neutral slate ramp with no home in the
 * theme, so it stays a set of literals on purpose — do not "unify" it with
 * theme.palette.text.* or the map's copy turns navy.
 */
export const INK = '#2C374A';
export const INK_SOFT = '#41506A';
export const MUTED = '#5A6980';
export const MUTED_SOFT = '#7A8699';
export const MUTED_FAINT = '#8894A8';
export const DISABLED = '#98A2B3';

/** Surfaces and hairlines. A near-white surface and hairline, distinct from the
 *  theme's pastel-blue `paper`/`divider` — same reason as the ink ramp above. */
export const SURFACE = '#fff';
export const BORDER = '#ECEEF2';
export const HOVER_TINT = '#F5F8FD';
export const TRACK = '#EEF1F6';

/** "This one is selected", for the sidebar: one soft brand tint behind brand-blue
 *  text, shared by its toggles, its picker tiles and the row of the open page. */
export const SELECT_TINT = `${BRAND}1A`;
export const SELECT_TINT_HOVER = `${BRAND}26`;

export const SHADOW_CARD = '0 1px 2px rgba(16,24,40,.04), 0 10px 24px -14px rgba(16,24,40,.14)';
export const SHADOW_LIFT = '0 1px 2px rgba(16,24,40,.05), 0 14px 26px -14px rgba(19,62,135,.3)';
export const SHADOW_LIFT_HOVER = '0 1px 2px rgba(16,24,40,.05), 0 22px 34px -16px rgba(19,62,135,.5)';

/**
 * Most controls on this page are built on `all: unset`, which strips the browser's
 * focus ring along with the rest of the button styling. Without this each of them
 * is invisible to a keyboard — you tab through the page with nothing to show where
 * you are. Spread it into the `sx` of anything focusable.
 */
export const FOCUS_RING = {
  '&:focus-visible': {
    outline: `3px solid ${BRAND}`,
    outlineOffset: '2px',
  },
};

/** A white card with a coloured cap along its top edge. */
export const cardSx = (color) => ({
  position: 'relative',
  bgcolor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '20px',
  boxShadow: SHADOW_CARD,
  overflow: 'hidden',
  '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: color },
});

/**
 * A card that rises towards the pointer. The topic bubbles and the resource rows
 * had this same lift-and-deepen pair written out twice with identical values.
 */
export const liftSx = {
  boxShadow: SHADOW_LIFT,
  transition: 'transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease, background-color .22s ease',
  '&:hover': { transform: 'translateY(-3px)', boxShadow: SHADOW_LIFT_HOVER },
};
